import React, { FC, useRef, useState }                   from 'react';
import { Formik, FormikBag, FormikConfig, FormikValues } from 'formik';
import { Button, Divider, Form }                         from 'semantic-ui-react';
import * as Yup                                          from 'yup'
import { useAuth0 }                                      from '@auth0/auth0-react';
import { Cell, Grid }                                    from 'styled-css-grid';

import { useFeatureFlags, useLogout, usePrefStates } from '../../../../hooks';
import { RouterUtility, runIfFunction }              from '../../../../util';
import { CORVAL_PRODUCT_NAME }                       from '../../../../constants/SystemEnums';
import { Checkbox }                                  from '../../../../views/components/forms';
import { CorvalPrivacyPolicyLink }                   from '../../../components/product/CorvalPrivacyPolicyLink';
import { LoadingIndicator }                          from '../../../components/util/loading/LoadingIndicator';

interface AuthZeroLoginFormProps {
	
	onSubmit?( values: FormikValues, formik: FormikBag<any, any> ): void,
	
	onLoginSuccess?( authToken: string ): void,
	
	onSignupSuccess?( authToken: string ): void,
	
	[ additionalProps: string ]: any
}

const defaultProps: AuthZeroLoginFormProps = {
	onSubmit:        () => {},
	onLoginSuccess:  () => {},
	onSignupSuccess: () => {},
}

export type AuthZeroLoginValues = FormikConfig<{
	consent: boolean,
}>;

const AuthZeroLoginForm: FC<AuthZeroLoginFormProps> = ( props ) => {
	
	let formikRef                                                         = useRef( {} as any ),
	    [ loading, setLoading ]                                           = useState( false ),
	    [ , prefs, prefEnums ]                                            = usePrefStates(),
	    { isLoading, isAuthenticated, loginWithPopup, loginWithRedirect } = useAuth0(),
	    { logout }                                                        = useLogout(),
	    featureFlags                                                      = useFeatureFlags();
	
	let consentedToUse = prefs.get( prefEnums.AGREED_TO_USAGE_TERMS )
	
	//setup form
	let formProps: AuthZeroLoginValues = {
		innerRef:         formikRef,
		isInitialValid:   consentedToUse,
		validateOnChange: true,
		initialValues:    {
			consent: consentedToUse,
		},
		validationSchema: Yup.object( {
			consent: Yup.boolean().notOneOf( [ false ], 'Consent is required to proceed' )
		} ),
		onSubmit:         props.onSubmit,
	};
	
	/* Update usage cookie pref */
	const handleUsageAgreementChanged = ( agreeState: boolean ) => {
		
		prefs.update( prefEnums.AGREED_TO_USAGE_TERMS, agreeState );
		
		formikRef.current.setFieldValue( 'consent', agreeState, true );
	}
	
	const handleInitiateAuthZeroLogin = async () => {
		
		setLoading( true );
		
		// Auth0 organization's use the tenant
		let organization = RouterUtility.getSubdomain();
		
		await loginWithRedirect( {
			display:     'popup',
			prompt:      'login',
			redirectUri: `${ window.location.origin }/login`
		} )
		
		/*
		Something breaks when using cacheLocation = "localstorage" and this popup. We provide
		the redirection login mechanism to avoid this, but this is how the login would work:
		
		await getAccessTokenWithPopup( {
			display: 'popup',
			prompt:  'login',
			
		} ).then( accessToken => {
			
			   if( accessToken ){
				
				   setLoading( false );
				
				   console.log( `[SESSION]: User successfully started session.` );
				
				   runIfFunction( props.onLoginSuccess, accessToken );
			   }
		   } )
		   .catch( e => {
			
			   setLoading( false );
			
		   } );
		   */
	}
	
	const handleInitiateAuthZeroLogout = async () => {
		
		await logout();
	}
	
	if( isLoading ){
		
		return (
			<LoadingIndicator
				label={ 'Initializing access...' }
				className={ 'position--relative margin-lg-bottom' }
				isActive={ true }
				inverted={ false }
				dimmer={ false }
			/>
		);
	}
	
	return (
		
		<Formik { ...formProps }>
			
			{ formik => (
				
				<Form onSubmit={ formik.handleSubmit } onReset={ formik.handleReset }>
					
					<Grid className={ 'text-center' } columns={ '1fr' } rows={ `auto auto` } rowGap={ '15px' }>
						<Cell middle center>
							
							{ !isAuthenticated ? (
								<Button type={ 'submit' }
								        fluid
								        primary
								        disabled={ !formik.isValid || loading }
								        onClick={ handleInitiateAuthZeroLogin }>Login</Button>
							) : (
								  <>
									  <LoadingIndicator
										  label={ 'Validating access...' }
										  className={ 'position--relative margin-lg-bottom' }
										  isActive={ true }
										  inverted={ false }
										  dimmer={ false }
									  />
									  <Button type={ 'button' }
									          size={ 'small' }
									          color={ 'grey' }
									          compact
									          basic
									          onClick={ handleInitiateAuthZeroLogout }>Logout</Button>
								  </>
							  ) }
						
						</Cell>
					</Grid>
					
					{ !isAuthenticated && (
						<>
							<Divider/>
							
							<Checkbox
								id={ 'consent' }
								name={ 'consent' }
								disabled={ loading }
								label={ <>I agree to the <CorvalPrivacyPolicyLink content={ `${ CORVAL_PRODUCT_NAME } privacy policy` } showPopup={ false }/>.</> }
								onChange={ ( event, data ) => handleUsageAgreementChanged( data.checked ) }
								onLabelClick={ handleUsageAgreementChanged }
							/>
						</>
					) }
				
				</Form>
			) }
		
		</Formik>
	);
};

AuthZeroLoginForm.defaultProps = defaultProps;

export { AuthZeroLoginForm };
